import DOMPurify from 'dompurify';
import { CSSProperties, useEffect, useState } from 'react';

/**
 * Renders an SVG inline so its `fill` / `stroke` / per-path attributes can
 * be styled via CSS (including `currentColor`). For non-SVG sources (PNG,
 * WebP, etc.) this falls back to a plain `<img>` so callers can use the same
 * component for any icon URL.
 *
 * SVG markup is fetched once per URL and stored in two caches:
 *  - `svgPromiseCache`: Promise cache — de-dupes in-flight fetches.
 *  - `svgResolvedCache`: Synchronous cache — lets `useState` initialize from
 *    a previously-resolved value so components that share a URL never render
 *    the loading placeholder at all after the first fetch.
 *
 * While a SVG is still loading we render an empty same-size <span> rather
 * than falling back to <img>, which would temporarily paint at the SVG's
 * intrinsic viewBox dimensions and cause a visible size flicker.
 */

const svgPromiseCache = new Map<string, Promise<string | null>>();
const svgResolvedCache = new Map<string, string | null>();

const isSvgUrl = (url: string) => /\.svg(\?.*)?$/i.test(url);

/**
 * Strip width/height/inline-style off the root <svg> so the element fills
 * its wrapper and inherits sizing from CSS. The viewBox is preserved, so
 * aspect ratio is maintained via the default `preserveAspectRatio`
 * (xMidYMid meet — equivalent to `object-fit: contain`).
 */
const normalizeSvgMarkup = (svg: string): string =>
  svg.replace(/<svg\b([^>]*)>/i, (_match: string, attrs: string) => {
    const cleaned = attrs
      .replace(/\swidth="[^"]*"/gi, '')
      .replace(/\sheight="[^"]*"/gi, '')
      .replace(/\sstyle="[^"]*"/gi, '');
    return `<svg${cleaned} width="100%" height="100%" style="display:block">`;
  });

const fetchSvg = (url: string): Promise<string | null> => {
  const cached = svgPromiseCache.get(url);
  if (cached) return cached;
  const promise = fetch(url, { credentials: 'omit' })
    .then((res) => (res.ok ? res.text() : null))
    .then((raw) => {
      if (!raw) return null;
      const sanitized = DOMPurify.sanitize(raw, {
        USE_PROFILES: { svg: true, svgFilters: true },
      });
      return normalizeSvgMarkup(sanitized);
    })
    .catch(() => null)
    .then((markup) => {
      // Populate the synchronous cache so future renders are instant.
      svgResolvedCache.set(url, markup);
      return markup;
    });
  svgPromiseCache.set(url, promise);
  return promise;
};

export interface InlineSvgIconProps {
  src: string;
  alt?: string;
  style?: CSSProperties;
  className?: string;
}

/**
 * `markup` state values:
 *  - `undefined` — SVG is still loading (show an empty placeholder).
 *  - `null`      — fetch failed (fall back to <img>).
 *  - `string`    — resolved markup ready for inline rendering.
 */
export const InlineSvgIcon = ({ src, alt, style, className }: InlineSvgIconProps) => {
  const isSvg = isSvgUrl(src);

  const [markup, setMarkup] = useState<string | null | undefined>(() => {
    if (!isSvg) return null;
    // Synchronously initialize from resolved cache (no-flash for repeat renders).
    if (svgResolvedCache.has(src)) return svgResolvedCache.get(src)!;
    return undefined; // still loading
  });

  useEffect(() => {
    if (!isSvg) {
      setMarkup(null);
      return;
    }
    // Already resolved synchronously — nothing to do.
    if (svgResolvedCache.has(src)) {
      setMarkup(svgResolvedCache.get(src)!);
      return;
    }
    let cancelled = false;
    void fetchSvg(src).then((m) => {
      if (!cancelled) setMarkup(m);
    });
    return () => {
      cancelled = true;
    };
  }, [src, isSvg]);

  // SVG still in-flight: render an invisible same-size placeholder so the
  // layout slot is held and no intrinsic-size flash occurs.
  if (isSvg && markup === undefined) {
    const { objectFit: _dropped, ...restStyle } = (style ?? {}) as CSSProperties & { objectFit?: string };
    return (
      <span
        aria-hidden
        className={className}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', ...restStyle }}
      />
    );
  }

  if (isSvg && markup) {
    // A <span> is display:inline by default, so width/height/objectFit from
    // callers have no effect. Force display:flex so the span acts as a block
    // sizing context and the SVG's "width:100% height:100%" fills it correctly.
    const { objectFit: _dropped, ...restStyle } = (style ?? {}) as CSSProperties & { objectFit?: string };
    return (
      <span
        role="img"
        aria-label={alt}
        className={className}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', ...restStyle }}
        dangerouslySetInnerHTML={{ __html: markup }}
      />
    );
  }

  // Non-SVG sources, or SVG fetch that permanently failed.
  return <img src={src} alt={alt} className={className} style={style} />;
};

export default InlineSvgIcon;
